'use client';
import React, { useState } from 'react';

interface FAQ {
  category: string;
  question: string;
  answer: string;
}

const faqs: FAQ[] = [
  {
    category: 'Pricing & Payment',
    question: 'How much do private tours in Morocco cost?',
    answer: 'Tour pricing depends on the duration, number of participants, destinations, and services included. A full-day private tour typically starts from $150-$300 per person. Airport transfers start from $40. Please contact David directly for a personalized quote tailored to your specific requirements.',
  },
  {
    category: 'Pricing & Payment',
    question: 'What payment methods do you accept?',
    answer: 'We accept bank transfers, PayPal, credit cards, and cash (USD, EUR, GBP, ILS). A deposit is required to confirm your booking, with the balance payable on the day of your tour. All pricing is agreed upon in advance with no hidden fees.',
  },
  {
    category: 'Private Tours',
    question: 'Are all tours completely private?',
    answer: 'Yes, absolutely. Every tour we offer is 100% private. We never combine different groups. Your tour is exclusively for you and your party, ensuring complete privacy, flexibility, and a fully personalized experience.',
  },
  {
    category: 'Private Tours',
    question: 'Can I customize my tour itinerary?',
    answer: 'Absolutely! Every itinerary is tailor-made to your interests, pace, and preferences. Simply tell David what you want to see and experience, and he will design the perfect journey for you. There are no fixed programs — everything is built around you.',
  },
  {
    category: 'Private Tours',
    question: 'How far in advance should I book?',
    answer: 'We recommend booking at least 2-4 weeks in advance to ensure availability, especially during peak travel seasons (March-May and September-November). However, we can often accommodate last-minute requests — contact David directly to check availability.',
  },
  {
    category: 'Jewish Heritage Tours',
    question: 'What Jewish heritage sites can I visit in Morocco?',
    answer: 'Morocco has an extraordinary Jewish heritage. Key sites include the Ibn Danan Synagogue and Jewish Cemetery in Fes, the Slat al-Azama Synagogue in Marrakech, the Beth-El Synagogue in Casablanca, the Museum of Moroccan Judaism, the mellahs of Fes, Marrakech, Meknes, and Essaouira, and numerous rabbinical shrines including Rabbi Amram Ben Diwan and Rabbi Haim Pinto.',
  },
  {
    category: 'Jewish Heritage Tours',
    question: 'Can you help with genealogy research in Morocco?',
    answer: 'Yes! David specializes in genealogy tours and can assist in tracing your family roots in Morocco. He can help locate ancestral communities, visit relevant cemeteries, and access historical records. Please share your family information in advance so David can prepare thoroughly.',
  },
  {
    category: 'Jewish Heritage Tours',
    question: 'Is kosher food available in Morocco?',
    answer: 'Yes, kosher food is available in Morocco, particularly in Casablanca, Marrakech, and Fes. David can assist with identifying kosher restaurants, arranging kosher meals, and planning your itinerary around kosher-friendly options. Please inform David of your dietary requirements when booking.',
  },
  {
    category: 'Airport Transfers',
    question: 'Which airports do you serve for transfers?',
    answer: 'We provide transfers to and from all major Moroccan airports including Casablanca Mohammed V International Airport (CMN), Marrakech Menara Airport (RAK), Fes-Saiss Airport (FEZ), Rabat-Salé Airport (RBA), Tangier Ibn Battuta Airport (TNG), Agadir Al Massira Airport (AGA), and all other Moroccan airports.',
  },
  {
    category: 'Airport Transfers',
    question: 'What happens if my flight is delayed?',
    answer: 'We monitor all incoming flights in real time. If your flight is delayed, David will adjust his arrival time accordingly at no extra charge. You will never be left waiting — we track your flight and are there when you land.',
  },
  {
    category: 'Cruise Excursions',
    question: 'Do you offer shore excursions for cruise passengers?',
    answer: 'Yes! We specialize in cruise shore excursions from all Moroccan ports including Casablanca, Tangier, Agadir, and Essaouira. We guarantee your return to the ship on time, and all excursions are private and fully customizable to your interests.',
  },
  {
    category: 'Cruise Excursions',
    question: 'How long are typical shore excursions?',
    answer: 'Shore excursions are designed to fit within your port time, typically 4-8 hours. We always factor in travel time and ensure you return to your ship well before departure. The itinerary is planned precisely around your ship\'s schedule.',
  },
  {
    category: 'Languages',
    question: 'What languages does David speak?',
    answer: 'David is fluent in English, French, Arabic, and Hebrew. This makes him the ideal guide for travelers from the USA, UK, Canada, France, Israel, and beyond. Tours can be conducted in any of these languages according to your preference.',
  },
  {
    category: 'Cancellation',
    question: 'What is your cancellation policy?',
    answer: 'We understand that travel plans can change. Cancellations made more than 7 days before the tour date receive a full refund of the deposit. Cancellations within 7 days may be subject to a partial fee. We always try to be flexible and can often reschedule rather than cancel.',
  },
  {
    category: 'Safety',
    question: 'Is Morocco safe for tourists?',
    answer: 'Morocco is generally a very safe country for tourists and is one of the most visited destinations in Africa. Traveling with a licensed private guide like David significantly enhances your safety and comfort. David knows all areas thoroughly and ensures you visit only safe, appropriate locations.',
  },
  {
    category: 'Safety',
    question: 'Are the vehicles safe and insured?',
    answer: 'All vehicles are modern, well-maintained, air-conditioned, and fully insured. They are regularly serviced and meet all Moroccan transportation safety standards. Your comfort and safety are our absolute priority.',
  },
  {
    category: 'Best Time to Visit',
    question: 'When is the best time to visit Morocco?',
    answer: 'The best times to visit Morocco are spring (March-May) and autumn (September-November) when temperatures are pleasant across the country. Summer (June-August) can be very hot in inland cities but is perfect for coastal destinations. Winter is ideal for the Sahara Desert and southern Morocco.',
  },
  {
    category: 'Practical Tips',
    question: 'What should I wear when visiting Morocco?',
    answer: 'Morocco is a Muslim country and modest dress is respectful, especially when visiting religious sites, medinas, and rural areas. We recommend covering shoulders and knees. Comfortable walking shoes are essential for medinas. David will advise on appropriate attire for specific sites.',
  },
  {
    category: 'Practical Tips',
    question: 'Can children join the tours?',
    answer: 'Absolutely! Morocco is a wonderful destination for families with children. David is experienced with family tours and adapts the pace and content to suit all ages. Child seats are available upon request. Special family-friendly itineraries can be designed.',
  },
  {
    category: 'Practical Tips',
    question: 'Are tours accessible for people with mobility challenges?',
    answer: 'We do our best to accommodate travelers with mobility challenges. While some medinas and historic sites have uneven terrain, David will plan routes that maximize accessibility and comfort. Please inform us of any specific needs when booking so we can prepare accordingly.',
  },
  {
    category: 'Practical Tips',
    question: 'Can I take photographs during the tour?',
    answer: 'Yes, photography is generally permitted at most sites. Some religious sites and cemeteries may have restrictions, which David will advise you about in advance. David is also happy to take photographs of your group throughout the tour.',
  },
  {
    category: 'Practical Tips',
    question: 'What is included in the tour price?',
    answer: 'Tour prices typically include: private transportation in a luxury air-conditioned vehicle, David as your licensed guide, hotel pickup and drop-off, and all driving. Entrance fees, meals, and accommodation are usually not included unless specifically stated in your custom itinerary.',
  },
  {
    category: 'Customization',
    question: 'Can you arrange accommodation and restaurant recommendations?',
    answer: 'Yes! David has extensive knowledge of Morocco\'s finest hotels, riads, and restaurants. He can provide personalized recommendations for every budget and preference, and can assist with reservations. For Jewish travelers, he can identify kosher-friendly establishments.',
  },
  {
    category: 'Customization',
    question: 'Can you organize multi-day tours across Morocco?',
    answer: 'Absolutely! Multi-day tours are our specialty. David can design comprehensive itineraries covering multiple cities, regions, and experiences over any number of days. From 2-day weekend escapes to 2-week grand Morocco tours — everything is possible.',
  },
  {
    category: 'Booking',
    question: 'How do I book a tour with MoroccoJewishTours?',
    answer: 'Booking is simple! Contact David directly via WhatsApp (+212668702424), email (dbdahantours@gmail.com), or through the contact form on this website. Describe your desired tour, dates, and group size, and David will respond promptly with a personalized proposal.',
  },
];

const categories = [
  'All',
  'Private Tours',
  'Jewish Heritage Tours',
  'Airport Transfers',
  'Cruise Excursions',
  'Pricing & Payment',
  'Safety',
  'Practical Tips',
  'Customization',
];

export default function FAQ() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const filtered =
    activeCategory === 'All'
      ? faqs
      : faqs?.filter((f) => f?.category === activeCategory);

  return (
    <section id="faq" className="section-padding bg-cream">
      <div className="container-luxury">
        {/* Header */}
        <div className="text-center mb-12 animate-on-scroll">
          <span className="tag-gold">FAQ</span>
          <h2 className="section-title mt-4 mb-4">
            Frequently Asked
            <br />
            <span className="text-gold italic">Questions</span>
          </h2>
          <div className="gold-divider" />
          <p className="section-subtitle mx-auto mt-4">
            Everything you need to know about touring Morocco with David.
            Can&apos;t find your answer? Contact us directly.
          </p>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-10 animate-on-scroll">
          {categories?.map((cat) => (
            <button
              key={cat}
              onClick={() => { setActiveCategory(cat); setOpenIndex(null); }}
              className={`font-sans text-xs font-bold uppercase tracking-wider px-4 py-2 border transition-all duration-300 ${
                activeCategory === cat
                  ? 'bg-gold text-navy-dark border-gold' :'bg-white text-navy border-gray-200 hover:border-gold hover:text-gold'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* FAQ Accordion */}
        <div className="max-w-4xl mx-auto space-y-3">
          {filtered?.map((faq, index) => (
            <div
              key={`${faq?.question}-${index}`}
              className={`faq-item ${
                openIndex === index ? 'border-gold shadow-gold' : 'border-gray-100 hover:border-gold/40'
              }`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-6 text-left"
              >
                <div className="flex items-start gap-4 flex-1">
                  <span className="font-sans text-xs font-bold text-gold uppercase tracking-wider bg-gold/10 px-2 py-1 flex-shrink-0 mt-0.5">
                    {faq?.category}
                  </span>
                  <span className="font-serif text-base font-semibold text-navy">
                    {faq?.question}
                  </span>
                </div>
                <div
                  className={`w-6 h-6 flex-shrink-0 ml-4 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-45' : ''
                  }`}
                >
                  <svg className="w-6 h-6 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
              </button>

              <div
                className="overflow-hidden transition-all duration-500 ease-in-out"
                style={{ maxHeight: openIndex === index ? '500px' : '0px' }}
              >
                <div className="px-6 pb-6">
                  <div className="pl-0 md:pl-32 border-t border-gold/20 pt-4">
                    <p className="font-sans text-sm text-gray-700 leading-relaxed">
                      {faq?.answer}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Still Have Questions */}
        <div className="text-center mt-14 animate-on-scroll">
          <div className="bg-navy p-8 md:p-12 max-w-2xl mx-auto">
            <h3 className="font-serif text-2xl font-bold text-white mb-3">
              Still Have Questions?
            </h3>
            <p className="font-sans text-white/70 mb-6">
              David is always available to answer your questions personally.
              Reach out via WhatsApp for the fastest response.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://wa.me/212668702424"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp"
              >
                WhatsApp David
              </a>
              <a
                href="mailto:dbdahantours@gmail.com"
                className="btn-outline-gold"
              >
                Email David
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
